import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import SessionModal from './parts/SessionModal';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import CustomPaginator from '../../components/CustomPaginator';

// ── Types ─────────────────────────────────────────────────────────────────────

interface School {
  _id?: string;
  id?: string;
  name: string;
  school_id_code: string;
  district?: string;
  state?: string;
  status?: string;
}

interface Session {
  _id?: string;
  id?: string;
  school_id: string;
  session: string;
  start_date: string;
  end_date: string;
  start_month: number;
  end_month: number;
  is_current: boolean;
  created_at: string;
  updated_at?: string;
}

const getSchoolId = (school: School): string => school._id || school.id || '';
const getSessionId = (session: Session): string => session._id || session.id || '';

// ── Component ─────────────────────────────────────────────────────────────────

export default function SessionManagement() {
  const [schoolsList, setSchoolsList] = useState<School[]>([]);
  const [allSessionsCache, setAllSessionsCache] = useState<Session[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);

  const [loadingSchools, setLoadingSchools] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const [schoolId, setSchoolId] = useState('');
  const [schoolSearch, setSchoolSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(true);

  // Pagination for the SESSIONS table
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsPageSize, setSessionsPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Pagination for the SCHOOL PICKER table
  const [schoolPage, setSchoolPage] = useState(1);
  const [schoolPageSize, setSchoolPageSize] = useState(10);

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);

  const toast = useRef<Toast>(null);

  // ── 1. Fetch Schools for the picker on Mount ──────────────────────────────
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const response = await apiClient.get('/api/schools');
        const data = response.data;
        let list: School[] = Array.isArray(data) ? data : data?.schools || [];
        setSchoolsList(list);
      } catch (error) {
        console.error('Failed to load schools', error);
      } finally {
        setLoadingSchools(false);
      }
    };
    fetchSchools();
  }, []);

  // ── 2. Fetch Sessions when School ID changes ──────────────────────────────
  useEffect(() => {
    if (schoolId) {
      fetchSessions();
    } else {
      setAllSessionsCache([]);
      setSessions([]);
      setTotalRecords(0);
    }
  }, [schoolId]);

  // Client-side slice for sessions table
  useEffect(() => {
    const start = (sessionsPage - 1) * sessionsPageSize;
    const end = start + sessionsPageSize;
    setSessions(allSessionsCache.slice(start, end));
  }, [sessionsPage, sessionsPageSize, allSessionsCache]);

  const fetchSessions = async (): Promise<void> => {
    if (!schoolId) return;
    setLoadingSessions(true);
    try {
      const response = await apiClient.get(`/api/sessions?school_id=${schoolId}`);
      const data = response.data;

      let sessionData: Session[] = [];
      if (Array.isArray(data)) {
        sessionData = data;
      } else if (data?.status === 'success' && Array.isArray(data.data)) {
        sessionData = data.data;
      }

      setAllSessionsCache(sessionData);
      setTotalRecords(sessionData.length);
      setSessionsPage(1);
    } catch (error: any) {
      console.error('Error fetching sessions:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error?.response?.data?.error || 'Failed to fetch sessions.',
        life: 3000,
      });
      setAllSessionsCache([]);
      setSessions([]);
      setTotalRecords(0);
    } finally {
      setLoadingSessions(false);
    }
  };

  const sessionsTotalPages = Math.max(1, Math.ceil(totalRecords / sessionsPageSize));

  // ── School picker helpers ─────────────────────────────────────────────────

  const selectedSchool = useMemo(
    () => schoolsList.find((s) => getSchoolId(s) === schoolId) || null,
    [schoolsList, schoolId]
  );

  const filteredSchools = useMemo(() => {
    const q = schoolSearch.trim().toLowerCase();
    if (!q) return schoolsList;
    return schoolsList.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.school_id_code?.toLowerCase().includes(q) ||
        s.district?.toLowerCase().includes(q) ||
        s.state?.toLowerCase().includes(q)
    );
  }, [schoolsList, schoolSearch]);

  // Reset school-picker pagination whenever the search query changes
  useEffect(() => {
    setSchoolPage(1);
  }, [schoolSearch]);

  const schoolTotalPages = Math.max(1, Math.ceil(filteredSchools.length / schoolPageSize));

  const pagedSchools = useMemo(() => {
    const start = (schoolPage - 1) * schoolPageSize;
    return filteredSchools.slice(start, start + schoolPageSize);
  }, [filteredSchools, schoolPage, schoolPageSize]);

  const handleSelectSchool = (school: School) => {
    setSchoolId(getSchoolId(school));
    setSessionsPage(1);
    setPickerOpen(false);
  };

  const handleChangeSchool = () => {
    setPickerOpen(true);
    setSchoolSearch('');
  };

  // ── Modal helpers ─────────────────────────────────────────────────────────
  const openCreate = () => {
    setSelectedSession(null);
    setModalVisible(true);
  };

  const openEdit = (session: Session) => {
    setSelectedSession(session);
    setModalVisible(true);
  };

  const handleModalSuccess = () => {
    setModalVisible(false);
    setSelectedSession(null);
    fetchSessions();
  };

  // ── Delete handler ────────────────────────────────────────────────────────
  const handleDelete = async (session: Session) => {
    const sessionId = getSessionId(session);
    if (!sessionId) return;

    const confirmed = window.confirm(`Are you sure you want to delete session "${session.session}"?`);
    if (!confirmed) return;

    try {
      await apiClient.delete(`/api/sessions/${sessionId}`);
      toast.current?.show({
        severity: 'success',
        summary: 'Deleted',
        detail: `Session "${session.session}" has been removed.`,
        life: 3000,
      });
      fetchSessions();
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error?.response?.data?.error || 'Failed to delete session.',
        life: 3000,
      });
    }
  };

  // ── Column templates (SESSIONS table) ─────────────────────────────────────
  const sessionNameBodyTemplate = (rowData: Session) => (
    <span
      style={{
        fontWeight: 700,
        color: '#1f2937',
        fontFamily: 'monospace',
        fontSize: 13,
        background: '#f9fafb',
        padding: '4px 8px',
        borderRadius: 6,
        border: '1px solid #e5e7eb',
        display: 'inline-block',
      }}
    >
      {rowData.session}
    </span>
  );

  const dateBodyTemplate = (rowData: Session, field: 'start_date' | 'end_date' | 'created_at') => {
    if (!rowData[field]) return <span style={{ color: '#9ca3af' }}>—</span>;
    return (
      <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
        {new Date(rowData[field]).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}
      </div>
    );
  };

  const monthBodyTemplate = (rowData: Session, field: 'start_month' | 'end_month') => {
    const monthName = new Date(0, rowData[field] - 1).toLocaleString('default', { month: 'short' });
    return (
      <div style={{ fontSize: 13, color: '#374151', fontWeight: 500, textTransform: 'capitalize' }}>
        {monthName}
      </div>
    );
  };

  const statusBodyTemplate = (rowData: Session) => {
    if (rowData.is_current) {
      return (
        <span
          style={{
            background: '#dcfce7',
            color: '#16a34a',
            padding: '4px 10px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <i className="pi pi-star-fill" style={{ fontSize: 10 }} /> Current
        </span>
      );
    }
    const isPast = new Date(rowData.end_date) < new Date();
    return (
      <span
        style={{
          background: isPast ? '#f3f4f6' : '#dbeafe',
          color: isPast ? '#6b7280' : '#1e40af',
          padding: '4px 10px',
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <i className={`pi ${isPast ? 'pi-history' : 'pi-clock'}`} style={{ fontSize: 10 }} />
        {isPast ? 'Past' : 'Upcoming'}
      </span>
    );
  };

  const actionsBodyTemplate = (rowData: Session) => (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
      <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-rounded"
        tooltip="Edit Session"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#3b82f6', width: 32, height: 32 }}
        onClick={() => openEdit(rowData)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-rounded"
        tooltip="Delete Session"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#ef4444', width: 32, height: 32 }}
        onClick={() => handleDelete(rowData)}
      />
    </div>
  );

  // ── Column templates (SCHOOL PICKER table) ────────────────────────────────
  const schoolCodeBodyTemplate = (s: School) => (
    <span
      style={{
        fontFamily: 'monospace',
        fontSize: 12,
        background: '#f9fafb',
        padding: '4px 8px',
        borderRadius: 6,
        fontWeight: 700,
        color: '#1f2937',
        border: '1px solid #e5e7eb',
        display: 'inline-block',
      }}
    >
      {s.school_id_code || '—'}
    </span>
  );

  const schoolNameBodyTemplate = (s: School) => (
    <div style={{ fontSize: 13, color: '#1f2937', fontWeight: 600 }}>{s.name || '—'}</div>
  );

  const schoolDistrictBodyTemplate = (s: School) => (
    <div style={{ fontSize: 13, color: '#374151', fontWeight: 500, textTransform: 'capitalize' }}>
      {s.district || '—'}
    </div>
  );

  const schoolStateBodyTemplate = (s: School) => (
    <div style={{ fontSize: 13, color: '#374151', fontWeight: 500, textTransform: 'capitalize' }}>
      {s.state || '—'}
    </div>
  );

  const schoolActionBodyTemplate = (s: School) => {
    const isSelected = getSchoolId(s) === schoolId;
    return (
      <button
        onClick={() => handleSelectSchool(s)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 14px',
          borderRadius: 10,
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all .2s',
          background: isSelected ? '#eff6ff' : 'linear-gradient(135deg,#3b82f6,#2563eb)',
          color: isSelected ? '#2563eb' : 'white',
          border: isSelected ? '1.5px solid #93c5fd' : 'none',
        }}
      >
        {isSelected && <i className="pi pi-check-circle" style={{ fontSize: 11 }} />}
        {isSelected ? 'Selected' : 'Select'}
      </button>
    );
  };

  // ── Stat cards (from full cache so counts stay accurate) ──────────────────
  const currentSession = allSessionsCache.find((s) => s.is_current);
  const statCards = [
    {
      label: 'Total Sessions',
      value: totalRecords,
      icon: 'pi-calendar',
      tint: 'rgba(59,130,246,.12)',
      iconColor: '#2563eb',
    },
    {
      label: 'Current Session',
      value: currentSession ? currentSession.session : 'None',
      icon: 'pi-star-fill',
      tint: 'rgba(16,185,129,.12)',
      iconColor: '#059669',
    },
    {
      label: 'Upcoming',
      value: allSessionsCache.filter((s) => !s.is_current && new Date(s.start_date) > new Date()).length,
      icon: 'pi-clock',
      tint: 'rgba(245,158,11,.12)',
      iconColor: '#d97706',
    },
    {
      label: 'Past Sessions',
      value: allSessionsCache.filter((s) => new Date(s.end_date) < new Date()).length,
      icon: 'pi-history',
      tint: 'rgba(107,114,128,.12)',
      iconColor: '#4b5563',
    },
  ];

  const showPicker = pickerOpen || !schoolId;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif', background: '#f8fafc' }}>
        <style>{`
          @keyframes slideIn { from { opacity:0; transform:translateX(-10px) } to { opacity:1; transform:translateX(0) } }
          @keyframes fadeInSlide { from { opacity:0; transform:translateY(16px) } to { opacity:1; transform:translateY(0) } }
          .p-datatable .p-datatable-tbody > tr { animation:slideIn .35s ease-out; transition:background .15s; }
          .p-datatable .p-datatable-tbody > tr:hover { background:rgba(65,105,225,.04)!important; box-shadow:inset 4px 0 0 0 #4169E1; }
          .p-button-text:hover { background:#f3f4f6!important; }
          .p-datatable .p-datatable-thead > tr > th {
            background:#f9fafb!important;
            color:#6b7280!important;
            font-size:11px!important;
            font-weight:700!important;
            text-transform:uppercase!important;
            letter-spacing:.8px!important;
            padding:16px 12px!important;
            border-bottom:1px solid #e5e7eb!important;
          }
          .p-datatable .p-datatable-tbody > tr > td {
            padding:16px 12px!important;
            border-bottom:1px solid #f3f4f6!important;
          }
          .p-datatable .p-datatable-tbody > tr:last-child > td { border-bottom:none!important; }
          .p-datatable .p-datatable-emptymessage td { padding:0!important; }
          .header-section { animation: fadeInSlide .5s ease-out; }
          .table-section { animation: fadeInSlide .55s ease-out; }
          .search-input-wrapper {
            display:flex; align-items:center; gap:10px; background:white; border-radius:16px;
            padding:12px 18px; margin-bottom:16px; box-shadow:0 2px 8px rgba(30,64,175,.08);
            border:1px solid rgba(59,130,246,.12); transition:border-color .2s,box-shadow .2s;
          }
          .search-input-wrapper:focus-within {
            border-color:rgba(59,130,246,.4); box-shadow:0 2px 12px rgba(30,64,175,.14);
          }
          .search-input-wrapper input[type="text"] {
            flex:1; border:none; outline:none; font-size:13px; font-weight:500; color:#1f2937; background:transparent;
          }
          .search-input-wrapper input[type="text"]::placeholder { color:#9ca3af; font-weight:400; }
          .search-clear-btn {
            background:none; border:none; cursor:pointer; color:#9ca3af; padding:2px 4px;
            border-radius:4px; display:flex; align-items:center; transition:color .2s;
          }
          .search-clear-btn:hover { color:#6b7280; }
        `}</style>

        {/* ── Header (kept) ── */}
        <div
          className="header-section"
          style={{
            background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
            borderRadius: 24,
            padding: 32,
            marginBottom: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 8px 32px rgba(40,85,174,0.15)',
            flexWrap: 'wrap',
            gap: 20,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div
              style={{
                background: 'rgba(255,255,255,0.25)',
                borderRadius: 16,
                padding: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              <i className="pi pi-calendar" style={{ fontSize: 24, color: 'white' }} />
            </div>
            <div>
              <h1
                style={{
                  color: 'white',
                  fontSize: 28,
                  fontWeight: 700,
                  margin: '0 0 6px 0',
                  letterSpacing: '-0.5px',
                }}
              >
                Session Management
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span
                  style={{
                    background: 'rgba(16,185,129,0.95)',
                    color: 'white',
                    padding: '6px 14px',
                    borderRadius: 14,
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {totalRecords} Sessions
                </span>
                <span style={{ color: 'rgba(255,255,255,0.95)', fontSize: 14, fontWeight: 500 }}>
                  Manage academic years and enrollments
                </span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {selectedSchool && !pickerOpen && (
              <div
                style={{
                  background: 'rgba(255,255,255,0.95)',
                  borderRadius: 12,
                  padding: '10px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                }}
              >
                <i className="pi pi-building" style={{ color: '#2855ae', fontSize: 15 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1f2937' }}>{selectedSchool.name}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{selectedSchool.school_id_code}</div>
                </div>
                <button
                  onClick={handleChangeSchool}
                  style={{
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: 8,
                    padding: '6px 12px',
                    cursor: 'pointer',
                    color: '#374151',
                    fontSize: 12,
                    fontWeight: 600,
                    marginLeft: 4,
                  }}
                >
                  Change
                </button>
              </div>
            )}

            <Button
              label="Add Session"
              icon="pi pi-plus"
              onClick={openCreate}
              disabled={!schoolId}
              style={{
                background: 'white',
                color: '#2855ae',
                border: 'none',
                borderRadius: 12,
                padding: '12px 24px',
                fontSize: 15,
                fontWeight: 600,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                cursor: schoolId ? 'pointer' : 'not-allowed',
                opacity: schoolId ? 1 : 0.7,
              }}
            />
          </div>
        </div>

        {/* ── School Picker ── */}
        {showPicker && (
          <div
            className="table-section"
            style={{
              background: 'white',
              borderRadius: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,.06)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              marginBottom: 24,
            }}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #f3f4f6',
                background: 'linear-gradient(135deg,#ffffff,#fafbfc)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <i className="pi pi-building" style={{ fontSize: 14, color: '#4169E1' }} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>Select a School</div>
                  <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                    {filteredSchools.length > 0
                      ? `${filteredSchools.length} school${filteredSchools.length !== 1 ? 's' : ''} available`
                      : 'Search by name, code, district or state'}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ padding: '16px 20px 0' }}>
              <div className="search-input-wrapper" style={{ marginBottom: 0 }}>
                <i className="pi pi-search" style={{ fontSize: 15, color: '#3b82f6', flexShrink: 0 }} />
                <input
                  type="text"
                  autoFocus
                  value={schoolSearch}
                  onChange={(e) => setSchoolSearch(e.target.value)}
                  placeholder={loadingSchools ? 'Loading schools...' : 'Search by name, code, district or state...'}
                  disabled={loadingSchools}
                />
                {schoolSearch && (
                  <button className="search-clear-btn" onClick={() => setSchoolSearch('')}>
                    <i className="pi pi-times" style={{ fontSize: 12 }} />
                  </button>
                )}
              </div>
            </div>

            <DataTable
              value={pagedSchools}
              loading={loadingSchools}
              dataKey="_id"
              emptyMessage={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    minHeight: 240,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      background: 'linear-gradient(135deg,#1e40af,#3b82f6,#60a5fa)',
                      borderRadius: 14,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 20,
                      boxShadow: '0 6px 18px rgba(59,130,246,.2)',
                    }}
                  >
                    <i className="pi pi-search" style={{ fontSize: 25, color: 'white' }} />
                  </div>
                  <h3
                    style={{
                      margin: 0,
                      color: '#1e40af',
                      fontSize: 18,
                      fontWeight: 700,
                      marginBottom: 8,
                      textAlign: 'center',
                    }}
                  >
                    No Schools Match
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: '#64748b',
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: 'center',
                      maxWidth: 360,
                      lineHeight: 1.6,
                    }}
                  >
                    Try a different name, code, district, or state.
                  </p>
                </div>
              }
            >
              <Column header="Code" body={schoolCodeBodyTemplate} style={{ width: 110 }} />
              <Column header="School Name" body={schoolNameBodyTemplate} />
              <Column header="District" body={schoolDistrictBodyTemplate} style={{ width: 140 }} />
              <Column header="State" body={schoolStateBodyTemplate} style={{ width: 140 }} />
              <Column header="Action" body={schoolActionBodyTemplate} style={{ width: 120 }} />
            </DataTable>

            {schoolTotalPages > 1 && (
              <CustomPaginator
                page={schoolPage}
                totalPages={schoolTotalPages}
                totalRecords={filteredSchools.length}
                pageSize={schoolPageSize}
                onPageChange={(newPage) => setSchoolPage(newPage)}
                onPageSizeChange={(newSize) => {
                  setSchoolPageSize(newSize);
                  setSchoolPage(1);
                }}
                showInfoBar={false}
              />
            )}
          </div>
        )}

        {/* ── Stat Cards ── */}
        {schoolId && !pickerOpen && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 16,
              marginBottom: 24,
            }}
          >
            {statCards.map((card) => (
              <div
                key={card.label}
                style={{
                  background: 'white',
                  borderRadius: 16,
                  padding: 20,
                  boxShadow: '0 2px 12px rgba(0,0,0,.06)',
                  border: '1px solid #e5e7eb',
                  transition: 'all .2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,.06)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: card.tint,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <i className={`pi ${card.icon}`} style={{ fontSize: 16, color: card.iconColor }} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>{card.label}</div>
                    <div
                      style={{
                        fontSize: 22,
                        color: '#1f2937',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {card.value}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Sessions Table ── */}
        {schoolId && !pickerOpen && (
          <div
            className="table-section"
            style={{
              background: 'white',
              borderRadius: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,.06)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              marginBottom: 24,
            }}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #f3f4f6',
                background: 'linear-gradient(135deg,#ffffff,#fafbfc)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <i className="pi pi-list" style={{ fontSize: 14, color: '#4169E1' }} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>Sessions Directory</div>
                  <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                    {totalRecords > 0
                      ? `Showing ${Math.min((sessionsPage - 1) * sessionsPageSize + 1, totalRecords)}–${Math.min(sessionsPage * sessionsPageSize, totalRecords)} of ${totalRecords} sessions`
                      : 'View and manage academic sessions for the selected school'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  onClick={fetchSessions}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 14px',
                    borderRadius: 10,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: 'white',
                    color: '#4169E1',
                    border: '1.5px solid #c7d2fe',
                    transition: 'all .2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#eff6ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                  }}
                >
                  <i className="pi pi-refresh" style={{ fontSize: 12 }} />
                  Refresh
                </button>
                {sessionsTotalPages > 0 && totalRecords > 0 && (
                  <div
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                      border: '1px solid rgba(65,105,225,.15)',
                    }}
                  >
                    <span style={{ fontSize: 13, color: '#4169E1', fontWeight: 600 }}>
                      Page {sessionsPage} of {sessionsTotalPages}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <DataTable
              value={sessions}
              loading={loadingSessions}
              dataKey="_id"
              emptyMessage={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    minHeight: 280,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      background: 'linear-gradient(135deg,#1e40af,#3b82f6,#60a5fa)',
                      borderRadius: 14,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 20,
                      boxShadow: '0 6px 18px rgba(59,130,246,.2)',
                    }}
                  >
                    <i className="pi pi-calendar" style={{ fontSize: 25, color: 'white' }} />
                  </div>
                  <h3
                    style={{
                      margin: 0,
                      color: '#1e40af',
                      fontSize: 18,
                      fontWeight: 700,
                      marginBottom: 8,
                      textAlign: 'center',
                    }}
                  >
                    No Sessions Found
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: '#64748b',
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: 'center',
                      maxWidth: 360,
                      lineHeight: 1.6,
                    }}
                  >
                    Add a session for this school to get started.
                  </p>
                </div>
              }
            >
              <Column header="Session" body={sessionNameBodyTemplate} style={{ width: 120 }} />
              <Column
                header="Start Date"
                body={(r: Session) => dateBodyTemplate(r, 'start_date')}
                style={{ width: 130 }}
              />
              <Column
                header="End Date"
                body={(r: Session) => dateBodyTemplate(r, 'end_date')}
                style={{ width: 130 }}
              />
              <Column
                header="Starts In"
                body={(r: Session) => monthBodyTemplate(r, 'start_month')}
                style={{ width: 100 }}
              />
              <Column
                header="Ends In"
                body={(r: Session) => monthBodyTemplate(r, 'end_month')}
                style={{ width: 100 }}
              />
              <Column header="Status" body={statusBodyTemplate} style={{ width: 120 }} />
              <Column
                header="Created"
                body={(r: Session) => dateBodyTemplate(r, 'created_at')}
                style={{ width: 120 }}
              />
              <Column header="Actions" body={actionsBodyTemplate} style={{ width: 100 }} />
            </DataTable>

            {sessionsTotalPages > 1 && (
              <CustomPaginator
                page={sessionsPage}
                totalPages={sessionsTotalPages}
                totalRecords={totalRecords}
                pageSize={sessionsPageSize}
                onPageChange={(newPage) => setSessionsPage(newPage)}
                onPageSizeChange={(newSize) => {
                  setSessionsPageSize(newSize);
                  setSessionsPage(1);
                }}
                showInfoBar={false}
              />
            )}
          </div>
        )}
      </div>

      {/* ── Session Modal ── */}
      <SessionModal
        visible={modalVisible}
        onHide={() => {
          setModalVisible(false);
          setSelectedSession(null);
        }}
        onSuccess={handleModalSuccess}
        session={selectedSession}
        defaultSchoolId={schoolId}
        toast={toast}
      />
    </>
  );
}